import { request } from 'utils/api';
import { GLADLY_APP_ID } from 'utils/env/client';

const DEFAULT_APP_ID = GLADLY_APP_ID || 'americandream.com';
const HR_APP_ID = 'americandream.com-hr';
const HR_ROUTES = ['/careers'];

export const getGladlyAppId = (pathname = window.location?.pathname || '') => {
  const isHrRoute = HR_ROUTES.some((route) => {
    return pathname === route || pathname.startsWith(`${route}/`);
  });

  return isHrRoute ? HR_APP_ID : DEFAULT_APP_ID;
};

export const initGladly = async (pathname) => {
  try {
    if (document.readyState !== 'complete') {
      await new Promise((resolve) => {
        window.addEventListener('load', resolve, { once: true });
      });
    }

    await window.Gladly?.init({
      appId: getGladlyAppId(pathname),
    });

    return true;
  } catch (error) {
    console.error('Error initializing Gladly:', error);
    throw new Error(`Failed to initialize Gladly: ${error.message}`);
  }
};

export const getGladlyUser = () => {
  return window.Gladly?.getUser?.() || null;
};

export const identifyGladlyUserWithJWT = async () => {
  try {
    const { name, email } = await request({
      path: '/1/gladly/user',
    });

    if (!email) {
      throw new Error('Email not returned from user endpoint');
    }

    if (!window.Gladly?.setUser) {
      throw new Error('Gladly SDK not initialized');
    }

    window.Gladly.setUser({
      name,
      jwt: async () => {
        const tokenResponse = await request({
          method: 'POST',
          path: '/1/gladly/jwt',
          body: { email },
        });

        if (!tokenResponse?.token) {
          throw new Error('JWT token not returned from endpoint');
        }

        return tokenResponse.token;
      },
    });

    return true;
  } catch (error) {
    console.error('Error identifying Gladly user:', error);
    throw error;
  }
};

export const setGladlyUser = async () => {
  try {
    const gladlyUser = getGladlyUser();
    if (gladlyUser) {
      return;
    }

    await identifyGladlyUserWithJWT();
  } catch (error) {
    console.error('Error setting Gladly user from store:', error);
    clearGladlyUser();
  }
};

export const clearGladlyUser = () => {
  try {
    window.Gladly?.clearUser?.();
    return true;
  } catch (error) {
    console.error('Error clearing Gladly user:', error);
    return false;
  }
};

export const showGladlyWidget = () => {
  try {
    window.Gladly?.show?.();
    return true;
  } catch (error) {
    console.error('Error showing Gladly widget:', error);
    return false;
  }
};

export const closeGladlyWidget = () => {
  try {
    window.Gladly?.close?.();
    return true;
  } catch (error) {
    console.error('Error closing Gladly widget:', error);
    return false;
  }
};
